
import React from 'react';
import moment, { Moment } from 'moment';

import { Button } from '@common/components/button';
import { Calendar } from '@common/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@common/components/ui/popover';
import { Message } from '@common/components/form/message';
import { combineClassNames } from '@common/utils/combineClassNames';
import { cn } from '@common/lib/utils';

type DateLike = Moment | Date | string | null | undefined;

const tokenPatternMap: Record<string, string> = {
  YYYY: '[0-9]{4}',
  YY: '[0-9]{2}',
  MM: '[0-9]{2}',
  M: '[0-9]{1,2}',
  DD: '[0-9]{2}',
  D: '[0-9]{1,2}',
};

// `MMM` / `MMMM` render the month as a name, so the field has to accept letters.
const MONTH_NAME_TOKEN = /M{3,}/;

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function createPatternFromFormat(format: string): string | undefined {
  // Native `pattern` is ASCII-oriented and rejects locale month names (März, août).
  // Leave it off for MMM/MMMM and let strict Moment parsing decide validity.
  if (MONTH_NAME_TOKEN.test(format)) return undefined;

  const tokens = format.match(/(YYYY|YY|MM|M|DD|D|[^YMD]+)/g);
  if (!tokens) return undefined;

  const pattern = tokens.map((token) => tokenPatternMap[token] ?? escapeRegExp(token)).join('');
  return pattern.length ? pattern : undefined;
}

function createAllowedInputPattern(format: string): RegExp | undefined {
  // Don't ASCII-gate month names — accents and abbreviations (März, août, jan.)
  // would be rejected before Moment can parse them.
  if (MONTH_NAME_TOKEN.test(format)) return undefined;

  const separators = Array.from(new Set(format.replace(/[A-Za-z]+/g, '').split('').filter(Boolean)));
  if (!separators.length) return /^[0-9]*$/;

  // Keep `-` last in the class so it cannot form an accidental range with `:` / space
  // (e.g. format `DD-MM-YYYY HH:mm` → separators `-`, ` `, `:`).
  const hasHyphen = separators.includes('-');
  const others = separators
    .filter((item) => item !== '-')
    .map((item) => item.replace(/[\]\\^]/g, '\\$&'));
  const characterClass = `0-9${others.join('')}${hasHyphen ? '-' : ''}`;
  return new RegExp(`^[${characterClass}]*$`);
}

type NativeInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
>;

export type DateInputProps = NativeInputProps & {
  value?: DateLike;
  defaultValue?: DateLike;
  onChange?: (date: Moment | null) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  showYearDropdown?: boolean;
  showMonthDropdown?: boolean;
  minDate?: DateLike;
  maxDate?: DateLike;
  warning?: string | boolean;
  error?: string | boolean;
  arrowPosition?: number;
  inline?: boolean;
  className?: string;
  calendarClassName?: string;
  format?: string;
  dropdownMode?: 'select' | 'scroll';
  fromYear?: number;
  toYear?: number;
  /**
   * Day + month only (recurring dates): drops the year from the calendar caption
   * and never offers a year dropdown. The value still carries a year — callers
   * that store day + month are expected to discard it.
   */
  hideYear?: boolean;
};

const toMoment = (value: DateLike): Moment | null => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value.clone() : null;
  if (value instanceof Date) {
    const result = moment(value);
    return result.isValid() ? result : null;
  }
  if (typeof value === 'string') {
    const result = moment(value);
    return result.isValid() ? result : null;
  }
  return null;
};

const areMomentsEqual = (a: Moment | null, b: Moment | null) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.isSame(b, 'day');
};

const toNativeDate = (value: Moment | null) => (value ? value.toDate() : undefined);

export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>((
  {
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    disabled,
    showMonthDropdown,
    showYearDropdown,
    minDate,
    maxDate,
    warning,
    error,
    arrowPosition: _arrowPosition,
    inline,
    className,
    calendarClassName,
    format = 'DD-MM-YYYY',
    dropdownMode,
    fromYear,
    toYear,
    hideYear,
    name,
    id,
    ...buttonProps
  },
  ref
) => {
  const minMoment = React.useMemo(() => toMoment(minDate), [minDate]);
  const maxMoment = React.useMemo(() => toMoment(maxDate), [maxDate]);

  const [internalValue, setInternalValue] = React.useState<Moment | null>(() => {
    const initial = toMoment(value ?? defaultValue);
    return initial;
  });

  React.useEffect(() => {
    if (value === undefined) return;
    const next = toMoment(value);
    setInternalValue((current) => (areMomentsEqual(current, next) ? current : next));
    setInputValue(next ? next.format(format) : '');
  }, [format, value]);

  const selectedMoment = internalValue;

  const disabledMatchers = React.useMemo(() => {
    const matchers = [];
    if (minMoment) {
      matchers.push({ before: minMoment.startOf('day').toDate() });
    }
    if (maxMoment) {
      matchers.push({ after: maxMoment.endOf('day').toDate() });
    }
    return matchers;
  }, [minMoment, maxMoment]);

  const inputPattern = React.useMemo(() => createPatternFromFormat(format), [format]);
  const allowedInputPattern = React.useMemo(() => createAllowedInputPattern(format), [format]);

  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(() => (selectedMoment ? selectedMoment.format(format) : ''));

  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const openedFromButtonRef = React.useRef(false);

  // Track if we're currently clicking inside the calendar (to prevent blur interference)
  const isClickingCalendarRef = React.useRef(false);

  // Ref to popover content for focus management
  const popoverContentRef = React.useRef<HTMLDivElement>(null);

  const calendarFromYear = React.useMemo(() => {
    if (fromYear) return fromYear;
    if (minMoment?.isValid()) return minMoment.year();
    return new Date().getFullYear() - 100;
  }, [fromYear, minMoment]);

  const calendarToYear = React.useMemo(() => {
    if (toYear) return toYear;
    if (maxMoment?.isValid()) return maxMoment.year();
    const baseYear = new Date().getFullYear() + 100;
    return Math.max(baseYear, calendarFromYear);
  }, [calendarFromYear, maxMoment, toYear]);

  const handleSelect = React.useCallback((date: Date | undefined) => {
    const nextMoment = date ? moment(date) : null;

    if (nextMoment && minMoment && nextMoment.isBefore(minMoment, 'day')) return;
    if (nextMoment && maxMoment && nextMoment.isAfter(maxMoment, 'day')) return;

    setInternalValue(nextMoment);
    onChange?.(nextMoment);

    if (!inline) {
      setOpen(false);
    }
    setInputValue(nextMoment ? nextMoment.format(format) : '');
    if (inputRef.current) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
      nativeInputValueSetter?.call(inputRef.current, nextMoment ? nextMoment.format(format) : '');
      const event = new Event('input', { bubbles: true });
      inputRef.current.dispatchEvent(event);
    }
  }, [format, inline, maxMoment, minMoment, onChange]);

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      onBlur?.();
      openedFromButtonRef.current = false;
      // Blur input when popover closes to prevent focus issues
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    }
  }, [onBlur]);

  const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    if (allowedInputPattern && !allowedInputPattern.test(nextValue)) return;
    setInputValue(nextValue);
    const parsed = moment(nextValue, format, true);
    if (parsed.isValid()) {
      const nextMoment = parsed;
      if (minMoment && nextMoment.isBefore(minMoment, 'day')) return;
      if (maxMoment && nextMoment.isAfter(maxMoment, 'day')) return;
      setInternalValue(nextMoment);
      onChange?.(nextMoment);
    } else if (nextValue === '') {
      setInternalValue(null);
      onChange?.(null);
    } else {
      setInternalValue(null);
    }
  }, [format, maxMoment, minMoment, onChange]);

  const handleInputBlur = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    // Don't process blur if we're clicking inside calendar
    if (isClickingCalendarRef.current) {
      return;
    }

    // Don't blur if focus is moving to calendar (relatedTarget is inside popover)
    // Use ref-based check instead of DOM traversal for React consistency
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget && popoverContentRef.current && popoverContentRef.current.contains(relatedTarget)) {
      return;
    }

    const parsed = moment(inputValue, format, true);
    if (!parsed.isValid()) {
      setInputValue(selectedMoment ? selectedMoment.format(format) : '');
    }
    onBlur?.(event);
  }, [format, inputValue, onBlur, selectedMoment]);

  const controlClassName = combineClassNames('Form__control', {
    'Form__control--warning': !!warning,
    'Form__control--invalid': !warning && !!error,
  });

  const captionLayout = hideYear
    ? (showMonthDropdown ? 'dropdown-months' : 'label')
    : (showMonthDropdown || showYearDropdown || dropdownMode === 'select')
      ? 'dropdown'
      : 'label';

  const calendarFormatters = React.useMemo(
    () => (hideYear ? { formatCaption: (date: Date) => moment(date).format('MMMM') } : undefined),
    [hideYear]
  );

  // Track the calendar month to keep it in sync with selected date
  const [calendarMonth, setCalendarMonth] = React.useState<Date | undefined>(
    () => toNativeDate(selectedMoment) ?? minMoment?.toDate() ?? new Date()
  );

  React.useEffect(() => {
    if (selectedMoment) {
      setCalendarMonth(selectedMoment.toDate());
    } else if (minMoment) {
      setCalendarMonth(minMoment.toDate());
    }
  }, [selectedMoment, minMoment]);

  const calendarNode = (
    <div
      onMouseDown={() => {
        isClickingCalendarRef.current = true;
      }}
      onMouseUp={() => {
        setTimeout(() => {
          isClickingCalendarRef.current = false;
        }, 0);
      }}
      className={inline ? 'tw-w-full tw-flex tw-justify-center' : ''}
    >
      <Calendar
        key={selectedMoment?.format('YYYY-MM') ?? 'no-date'}
        mode="single"
        selected={toNativeDate(selectedMoment)}
        onSelect={handleSelect}
        captionLayout={captionLayout}
        formatters={calendarFormatters}
        disabled={disabledMatchers.length ? disabledMatchers : undefined}
        month={calendarMonth}
        onMonthChange={setCalendarMonth}
        defaultMonth={toNativeDate(selectedMoment) ?? minMoment?.toDate()}
        className={cn(calendarClassName, `${!inline ? 'calendar-input' : 'calendar-inline'}`)}
        fromYear={calendarFromYear}
        toYear={calendarToYear}
      />
    </div>
  );

  return (
    <>
      {name ? (
        <input
          type="hidden"
          name={name}
          id={id ? `${id}-value` : undefined}
          value={selectedMoment?.isValid() ? selectedMoment.toISOString() : ''}
        />
      ) : null}
      {!inline ? (
        <div className="tw-relative date-picker-input">
          <Popover
            open={!disabled && open}
            onOpenChange={handleOpenChange}
          >
            <input
              ref={inputRef}
              id={id}
              name={name}
              type="text"
              value={inputValue}
              placeholder={placeholder}
              disabled={disabled}
              inputMode={MONTH_NAME_TOKEN.test(format) ? 'text' : 'numeric'}
              className={combineClassNames(controlClassName, `tw-h-9 !tw-w-[calc(100%-58px)] !tw-pl-10 tw-text-sm tw-leading-none
                tw-bg-transparent focus:tw-outline-none tw-cursor-text`, className)}
              onChange={handleInputChange}
              onFocus={(event) => {
                onFocus?.(event);
                if (!disabled && !open && !openedFromButtonRef.current) {
                  setOpen(true);
                }
                openedFromButtonRef.current = false;
              }}
              onBlur={handleInputBlur}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  if (!disabled) {
                    setOpen(true);
                  }
                }
              }}
              pattern={inputPattern}
              {...buttonProps}
            />
            <PopoverTrigger asChild>
              <Button
                buttonType="button"
                type="toolbar"
                size="small"
                disabled={disabled}
                onClick={() => {
                  openedFromButtonRef.current = true;
                  if (!disabled && !open) {
                    setOpen(true);
                  }
                }}
                className={`tw-absolute tw-left-2 tw-top-1/2 -tw-translate-y-1/2 tw-items-center tw-justify-center !tw-p-0
                  tw-rounded-full hover:!tw-bg-transparent tw-pointer-events-auto`}
                icon="calendar_today"
                noFilledIcon
              />
            </PopoverTrigger>
            <PopoverContent
              ref={popoverContentRef}
              className={cn('tw-w-auto !tw-p-0', calendarClassName)}
              align="start"
              onOpenAutoFocus={(event) => {
                event.preventDefault();
              }}
              onInteractOutside={(event) => {
                const target = event.target as HTMLElement;
                if (inputRef.current && (inputRef.current === target || inputRef.current.contains(target))) {
                  event.preventDefault();
                }
              }}
            >
              {calendarNode}
            </PopoverContent>
          </Popover>
        </div>
      ) : (
        <div className={combineClassNames('date-picker-inline', className)}>
          {calendarNode}
        </div>
      )}
      <Message error={error} warning={warning} />
    </>
  );
});

DateInput.displayName = 'DateInput';
